/**
 * Datos de ejemplo del modo demostracion.
 *
 * Se generan a partir de la fecha actual, de modo que la temporada, la quincena
 * y las competiciones activas siempre son coherentes con "hoy" y la demo no
 * caduca. El generador aleatorio esta sembrado con una constante, asi que dos
 * arranques producen exactamente los mismos datos.
 *
 * Nada de esto llega a produccion: en cuanto hay credenciales de Supabase,
 * `src/data/index.ts` sirve el repositorio real.
 */

import { BADGE_CATALOG } from '@/config/badges';
import { EXERCISE_CATALOG } from '@/config/scoring';
import { assignWeightClass } from '@/lib/categories';
import {
  FORTNIGHTS_PER_SEASON,
  WEEKS_PER_SEASON,
  defaultSeasonName,
  quarterOf,
  quarterStart,
  seasonFortnightNumber,
  seasonFortnights,
} from '@/lib/dates';
import type { AuthProvider } from '@/data/types';
import { computeScore } from '@/lib/scoring';
import { rankForPoints } from '@/lib/ranks';
import type {
  AuditLogEntry,
  Badge,
  Competition,
  CompetitionTheme,
  Enrollment,
  Exercise,
  Follow,
  ForumPost,
  GenderCategory,
  Profile,
  Report,
  SubmissionComment,
  SubmissionReaction,
  Season,
  Submission,
  UserBadge,
} from '@/types/domain';

export interface DemoData {
  profiles: Profile[];
  seasons: Season[];
  competitions: Competition[];
  exercises: Exercise[];
  enrollments: Enrollment[];
  submissions: Submission[];
  badges: Badge[];
  userBadges: UserBadge[];
  follows: Follow[];
  reports: Report[];
  submissionReactions: SubmissionReaction[];
  submissionComments: SubmissionComment[];
  forumPosts: ForumPost[];
  auditLog: AuditLogEntry[];
  /** Credenciales de las cuentas de ejemplo mostradas en la pantalla de acceso. */
  demoAccounts: { id: string; email: string; alias: string; role: Profile['role'] }[];
  /**
   * Correo y proveedor de cada cuenta.
   *
   * Antes el correo se inventaba a partir del alias, asi que una cuenta recien
   * creada —sin alias todavia— se quedaba en "@calipeak.demo" y no habia forma
   * de saber en que sesion estabas. Ahora se guarda de verdad.
   */
  accounts: { userId: string; email: string; provider: AuthProvider }[];
}

/** Generador determinista: la demo es siempre la misma. */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = mulberry32(0x5eed1a);

const pick = <T,>(list: readonly T[]): T => list[Math.floor(rand() * list.length)];
const between = (min: number, max: number): number => min + rand() * (max - min);
const intBetween = (min: number, max: number): number => Math.floor(between(min, max + 1));

const LOCATIONS: { country: string; cities: string[] }[] = [
  { country: 'ES', cities: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Málaga'] },
  { country: 'MX', cities: ['Ciudad de México', 'Guadalajara', 'Monterrey'] },
  { country: 'AR', cities: ['Buenos Aires', 'Córdoba', 'Rosario'] },
  { country: 'CO', cities: ['Bogotá', 'Medellín'] },
  { country: 'US', cities: ['New York', 'Los Angeles', 'Austin'] },
  { country: 'GB', cities: ['London', 'Manchester'] },
  { country: 'FR', cities: ['Paris', 'Lyon'] },
  { country: 'DE', cities: ['Berlin', 'Múnich'] },
];

const ALIAS_PREFIX = [
  'Leonidas', 'Agoge', 'Hoplon', 'Doru', 'Xiphos', 'Kleos', 'Aspis', 'Lambda',
  'Thermo', 'Krypteia', 'Phobos', 'Enyo', 'Ares', 'Nike', 'Atlas', 'Titan',
  'Bronze', 'Iron', 'Granite', 'Onyx',
];
const ALIAS_SUFFIX = [
  'Lever', 'Planche', 'Flag', 'Front', 'Grip', 'Hold', 'Pull', 'Bar',
  'Rings', 'Static', 'Core', 'Apex', 'Peak', 'Edge', 'Forge', 'Vault',
];

function makeAlias(index: number): string {
  const prefix = ALIAS_PREFIX[index % ALIAS_PREFIX.length];
  const suffix = ALIAS_SUFFIX[Math.floor(index / ALIAS_PREFIX.length) % ALIAS_SUFFIX.length];
  return `${prefix}${suffix}${index < 10 ? '' : index}`;
}

function iso(date: Date): string {
  return date.toISOString();
}

function daysAgo(now: Date, days: number): Date {
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
}

// ---------------------------------------------------------------------------
// Plantillas de competicion, una por tematica (RF-COMP-07)
// ---------------------------------------------------------------------------

export interface CompetitionTemplate {
  slug: string;
  theme: CompetitionTheme;
  title: { es: string; en: string };
  description: { es: string; en: string };
  rules: { es: string; en: string };
  exerciseCodes: string[];
  hashtag: string;
  enduranceFormat?: 'AMRAP' | 'FOR_TIME';
  enduranceTargetSeconds?: number;
}

/**
 * Plantillas de competicion.
 *
 * Regla de coherencia: una competicion admite EXACTAMENTE los ejercicios que
 * anuncia su nombre. Si se llama "Dominadas estrictas", solo entran dominadas;
 * un muscle-up no es una dominada, y meterlos en la misma tabla arruina la
 * clasificacion porque el coeficiente premia al que hace el movimiento mas
 * caro aunque el otro haya hecho mejor marca en lo que pedia la prueba. Las
 * que admiten varios ejercicios lo dicen en el titulo ("Estaticos de elite").
 *
 * Ninguna prueba se hace en anillas.
 */
export const TEMPLATES: CompetitionTemplate[] = [
  // --- 0. Estatico fundamental ---------------------------------------------
  {
    slug: 'front-lever-hold',
    theme: 'STATIC',
    title: { es: 'Aguante de Front Lever', en: 'Front Lever Hold' },
    description: {
      es: 'Aguanta el front lever el máximo tiempo posible. El cuerpo debe permanecer horizontal y alineado.',
      en: 'Hold the front lever for as long as you can. The body must stay horizontal and aligned.',
    },
    rules: {
      es: 'Solo front lever: cualquier otra posición invalida el intento. El cronómetro empieza cuando el cuerpo alcanza la horizontal y para cuando la cadera cae por debajo de la línea de los hombros. Se permite lastre, declarado y visible.',
      en: 'Front lever only: any other position voids the attempt. The clock starts when the body reaches horizontal and stops when the hips drop below shoulder line. Added weight is allowed if declared and visible.',
    },
    exerciseCodes: ['front_lever'],
    hashtag: '#CaliPeakFrontLever',
  },

  // --- 1. Dinamico fundamental ---------------------------------------------
  {
    slug: 'strict-pull-ups',
    theme: 'DYNAMIC',
    title: { es: 'Dominadas estrictas', en: 'Strict Pull-ups' },
    description: {
      es: 'Máximo número de dominadas estrictas en una sola serie, sin balanceo ni kipping.',
      en: 'Maximum strict pull-ups in a single set, no swinging or kipping.',
    },
    rules: {
      es: 'Solo dominadas pronas estrictas: un muscle-up, una dominada supina o cualquier otra variante invalida la serie. Extensión completa de codos abajo y barbilla por encima de la barra arriba. Sin balanceo. Sin lastre.',
      en: 'Strict pronated pull-ups only: a muscle-up, a chin-up or any other variation voids the set. Full elbow extension at the bottom and chin over the bar at the top. No swinging. No added weight.',
    },
    exerciseCodes: ['pull_up'],
    hashtag: '#CaliPeakPullUps',
  },

  // --- 2. Estatico con lastre ----------------------------------------------
  {
    slug: 'weighted-reps',
    theme: 'DYNAMIC_WEIGHTED',
    title: { es: 'Repeticiones lastradas', en: 'Weighted Reps' },
    description: {
      es: 'Máximas repeticiones con lastre: dominadas o fondos en paralelas. El peso debe verse claramente antes o después de la serie.',
      en: 'Maximum reps with added weight: pull-ups or parallel bar dips. The load must be clearly visible before or after the set.',
    },
    rules: {
      es: 'Lastre obligatorio (chaleco, cinturón con discos o mochila). Muestra el peso en cámara antes o después de la serie. Recorrido completo en cada repetición: brazos extendidos abajo y barbilla por encima de la barra, o codos bloqueados en fondos.',
      en: 'Added weight is mandatory (vest, dip belt or backpack). Show the load on camera before or after the set. Full range on every rep: arms extended at the bottom and chin over the bar, or elbows locked out on dips.',
    },
    exerciseCodes: ['weighted_pull_up', 'weighted_dip'],
    hashtag: '#CaliPeakWeightedReps',
  },

  // --- 3. Resistencia AMRAP -------------------------------------------------
  {
    slug: 'dips-amrap',
    theme: 'ENDURANCE',
    title: { es: 'AMRAP 10 min de fondos', en: '10 min Dips AMRAP' },
    description: {
      es: 'Máximo de repeticiones de fondos en paralelas en 10 minutos. Puedes descansar, el cronómetro no para.',
      en: 'As many parallel bar dips as possible in 10 minutes. You may rest; the clock keeps running.',
    },
    rules: {
      es: 'Cronómetro visible en el vídeo. Rango completo: hombros por debajo de los codos abajo, bloqueo arriba. Sin lastre.',
      en: 'Timer visible in the video. Full range: shoulders below elbows at the bottom, lockout at the top. No added weight.',
    },
    exerciseCodes: ['dips_amrap_10'],
    hashtag: '#CaliPeakDipsAMRAP',
    enduranceFormat: 'AMRAP',
    enduranceTargetSeconds: 600,
  },

  // --- 4. Resistencia por tiempo -------------------------------------------
  {
    slug: 'hundred-pull-ups',
    theme: 'ENDURANCE',
    title: { es: '100 dominadas por tiempo', en: '100 Pull-ups for Time' },
    description: {
      es: 'Completa 100 dominadas en el menor tiempo posible. Objetivo de referencia: 6 minutos.',
      en: 'Complete 100 pull-ups as fast as you can. Reference target: 6 minutes.',
    },
    rules: {
      es: 'Cronómetro visible durante todo el intento, en un solo plano continuo. Sin lastre.',
      en: 'Timer visible throughout the attempt, in one continuous shot. No added weight.',
    },
    exerciseCodes: ['pull_up_100_for_time'],
    hashtag: '#CaliPeak100PullUps',
    enduranceFormat: 'FOR_TIME',
    enduranceTargetSeconds: 360,
  },

  // --- 5. Estatico de equilibrio -------------------------------------------
  {
    slug: 'handstand-hold',
    theme: 'STATIC',
    title: { es: 'Pino libre', en: 'Freestanding Handstand' },
    description: {
      es: 'Máximo tiempo en pino libre, sin apoyo en pared.',
      en: 'Longest freestanding handstand, no wall support.',
    },
    rules: {
      es: 'Solo pino libre. Sin contacto con la pared ni con ningún apoyo. El cronómetro para al primer apoyo o caída.',
      en: 'Freestanding handstand only. No contact with a wall or any support. The clock stops at the first touch or fall.',
    },
    exerciseCodes: ['handstand'],
    hashtag: '#CaliPeakHandstand',
  },

  // --- 6. Estatico lateral --------------------------------------------------
  {
    slug: 'human-flag',
    theme: 'STATIC',
    title: { es: 'Bandera humana', en: 'Human Flag' },
    description: {
      es: 'Aguanta la bandera humana con el cuerpo horizontal.',
      en: 'Hold the human flag with the body horizontal.',
    },
    rules: {
      es: 'Solo bandera humana. Cuerpo horizontal y piernas juntas y extendidas. El cronómetro para cuando las caderas caen.',
      en: 'Human flag only. Body horizontal, legs together and extended. The clock stops when the hips drop.',
    },
    exerciseCodes: ['human_flag'],
    hashtag: '#CaliPeakHumanFlag',
  },

  // --- 7. Dinamico explosivo ------------------------------------------------
  {
    slug: 'muscle-up-max',
    theme: 'DYNAMIC',
    title: { es: 'Muscle-ups al fallo', en: 'Muscle-ups to Failure' },
    description: {
      es: 'Máximo número de muscle-ups estrictos en barra, en una sola serie.',
      en: 'Maximum strict bar muscle-ups in a single set.',
    },
    rules: {
      es: 'Solo muscle-up en barra. Transición limpia, sin kipping excesivo. Bloqueo de codos arriba. Sin lastre.',
      en: 'Bar muscle-up only. Clean transition, no excessive kipping. Elbow lockout at the top. No added weight.',
    },
    exerciseCodes: ['muscle_up_bar'],
    hashtag: '#CaliPeakMuscleUp',
  },

  // --- 8. Estaticos de elite -----------------------------------------------
  {
    slug: 'elite-statics',
    theme: 'STATIC',
    title: { es: 'Estáticos de élite', en: 'Elite Statics' },
    description: {
      es: 'Las tres posiciones más duras de la calistenia: planche extendida, maltese y victorian. Elige una y aguanta.',
      en: 'The three hardest positions in calisthenics: full planche, maltese and victorian. Pick one and hold it.',
    },
    rules: {
      es: 'Solo planche extendida, maltese o victorian. Cuerpo alineado y brazos bloqueados; una planche con la cadera por encima de los hombros no cuenta. El cronómetro para al perder la posición.',
      en: 'Full planche, maltese or victorian only. Body aligned and arms locked; a planche with hips above shoulder line does not count. The clock stops when the position breaks.',
    },
    exerciseCodes: ['full_planche', 'maltese', 'victorian'],
    hashtag: '#CaliPeakEliteStatics',
  },

  // --- 9. Empuje de elite ---------------------------------------------------
  {
    slug: 'planche-push-ups',
    theme: 'DYNAMIC',
    title: { es: 'Flexiones en planche', en: 'Planche Push-ups' },
    description: {
      es: 'Máximo de flexiones en planche. Uno de los empujes más difíciles que existen.',
      en: 'Maximum planche push-ups. One of the hardest pressing movements there is.',
    },
    rules: {
      es: 'Solo flexión en planche: los pies no tocan el suelo en ningún momento. Bloqueo completo arriba y pecho cerca del suelo abajo.',
      en: 'Planche push-up only: the feet never touch the ground. Full lockout at the top, chest close to the floor at the bottom.',
    },
    exerciseCodes: ['planche_push_up'],
    hashtag: '#CaliPeakPlanchePushUps',
  },

  // --- 10. Tiron de elite ---------------------------------------------------
  {
    slug: 'front-lever-touch',
    theme: 'DYNAMIC',
    title: { es: 'Front lever touch', en: 'Front Lever Touch' },
    description: {
      es: 'Repeticiones de front lever touch: desde la suspensión, tira hasta el front lever y toca la barra.',
      en: 'Front lever touch reps: from a dead hang, pull into front lever and touch the bar.',
    },
    rules: {
      es: 'Solo front lever touch. Cada repetición pasa por la horizontal y la barra toca el cuerpo. Sin balanceo entre repeticiones.',
      en: 'Front lever touch only. Every rep passes through horizontal and the bar touches the body. No swinging between reps.',
    },
    exerciseCodes: ['front_lever_touch'],
    hashtag: '#CaliPeakFrontLeverTouch',
  },

  // --- 11. Empuje invertido -------------------------------------------------
  {
    slug: 'hspu-max',
    theme: 'DYNAMIC',
    title: { es: 'Pino-flexiones al fallo', en: 'Handstand Push-ups to Failure' },
    description: {
      es: 'Máximo de pino-flexiones en una serie. Rango completo, cabeza al suelo y bloqueo arriba.',
      en: 'Maximum handstand push-ups in a single set. Full range, head to the floor and lockout at the top.',
    },
    rules: {
      es: 'Solo pino-flexión. Se admite apoyo en pared declarándolo en las notas. Cada repetición toca el suelo con la cabeza y termina con los codos bloqueados.',
      en: 'Handstand push-up only. Wall support is allowed if declared in the notes. Every rep touches the floor with the head and finishes with elbows locked.',
    },
    exerciseCodes: ['hspu'],
    hashtag: '#CaliPeakHSPU',
  },

  // --- 12. Tiron a un brazo -------------------------------------------------
  {
    slug: 'one-arm-pull-up',
    theme: 'DYNAMIC',
    title: { es: 'Dominadas a un brazo', en: 'One-arm Pull-ups' },
    description: {
      es: 'Máximo de dominadas a un brazo. Suman las repeticiones de ambos brazos.',
      en: 'Maximum one-arm pull-ups. Reps from both arms count together.',
    },
    rules: {
      es: 'Solo dominada a un brazo, sin ayuda de la otra mano ni de goma elástica. Extensión completa abajo y barbilla por encima de la barra arriba.',
      en: 'One-arm pull-up only, with no help from the other hand and no resistance band. Full extension at the bottom and chin over the bar at the top.',
    },
    exerciseCodes: ['one_arm_pull_up'],
    hashtag: '#CaliPeakOneArm',
  },
];

// ---------------------------------------------------------------------------
// Construccion del conjunto de datos
// ---------------------------------------------------------------------------

export function buildDemoData(now: Date = new Date()): DemoData {
  const exercises = buildExercises();
  const badges = buildBadges();
  const seasons = buildSeasons(now);
  const currentSeason = seasons.find((s) => s.status === 'ACTIVE')!;
  const profiles = buildProfiles(now);
  const { competitions, enrollments, submissions } = buildCompetitions(
    now,
    currentSeason,
    exercises,
    profiles,
  );

  // Los puntos del perfil son la suma de sus envios aprobados, para que el
  // ranking y el perfil no puedan contarse cosas distintas.
  const pointsByUser = new Map<string, number>();
  for (const submission of submissions) {
    if (submission.status !== 'APROBADO') continue;
    const points = submission.final_score ?? 0;
    pointsByUser.set(submission.user_id, (pointsByUser.get(submission.user_id) ?? 0) + points);
  }
  for (const profile of profiles) {
    const points = pointsByUser.get(profile.id) ?? 0;
    profile.season_points = points;
    profile.rank_level = rankForPoints(points).level;
    profile.best_rank_level = Math.max(profile.rank_level, profile.best_rank_level);
  }

  const userBadges = buildUserBadges(now, profiles, competitions, submissions, badges);
  const follows = buildFollows(profiles);
  const auditLog = buildAuditLog(now, profiles, competitions);

  return {
    profiles,
    seasons,
    competitions,
    exercises,
    enrollments,
    submissions,
    badges,
    userBadges,
    follows,
    reports: [],
    submissionReactions: [],
    submissionComments: [],
    forumPosts: demoForumPosts(profiles),
    auditLog,
    // Las tres cuentas fijas, una por rol, que ofrece la pantalla de acceso.
    demoAccounts: ['demo-admin', 'demo-mod', 'demo-athlete']
      .map((id) => profiles.find((p) => p.id === id))
      .filter((p): p is Profile => Boolean(p))
      .map((p) => ({
        id: p.id,
        email: `${p.alias.toLowerCase()}@calipeak.demo`,
        alias: p.alias,
        role: p.role,
      })),
    // Cada cuenta con su correo y su procedencia. Las sembradas entraron por
    // correo; la de Google se crea al pulsar el boton.
    accounts: ['demo-admin', 'demo-mod', 'demo-athlete']
      .map((id) => profiles.find((p) => p.id === id))
      .filter((p): p is Profile => Boolean(p))
      .map((p) => ({
        userId: p.id,
        email: `${p.alias.toLowerCase()}@calipeak.demo`,
        provider: 'EMAIL' as AuthProvider,
      })),
  };
}

function buildExercises(): Exercise[] {
  return EXERCISE_CATALOG.map((seed) => ({
    id: `ex-${seed.code}`,
    code: seed.code,
    name_i18n: seed.name,
    theme: seed.theme,
    difficulty_coefficient: seed.difficulty,
    reference_mark: seed.reference,
    allows_weight: seed.allowsWeight,
    version: 1,
    active: true,
    demo_video_url: null,
  }));
}

function buildBadges(): Badge[] {
  return BADGE_CATALOG.map((seed) => ({
    id: `badge-${seed.code}`,
    code: seed.code,
    // El nombre visible sale de los ficheros de idioma (`badges.<code>`);
    // aqui solo viaja la clave, para no duplicar traducciones.
    name_i18n: { es: seed.code, en: seed.code },
    description_i18n: { es: `${seed.code}_desc`, en: `${seed.code}_desc` },
    icon_url: null,
    criteria_type: seed.criteriaType,
    threshold: seed.threshold ?? null,
    difficulty: seed.difficulty,
    value: seed.value,
  }));
}

function buildSeasons(now: Date): Season[] {
  const currentQuarter = quarterOf(now);
  const year = now.getUTCFullYear();

  const make = (quarter: 1 | 2 | 3 | 4, y: number, status: Season['status']): Season => {
    const start = quarterStart(y, quarter);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + WEEKS_PER_SEASON * 7 - 1);
    return {
      id: `season-${y}-q${quarter}`,
      name_i18n: defaultSeasonName(quarter, y),
      quarter,
      year: y,
      start_date: iso(start),
      end_date: iso(end),
      carryover_pct: 40,
      status,
    };
  };

  const previousQuarter = (currentQuarter === 1 ? 4 : currentQuarter - 1) as 1 | 2 | 3 | 4;
  const previousYear = currentQuarter === 1 ? year - 1 : year;
  const nextQuarter = (currentQuarter === 4 ? 1 : currentQuarter + 1) as 1 | 2 | 3 | 4;
  const nextYear = currentQuarter === 4 ? year + 1 : year;

  return [
    make(previousQuarter, previousYear, 'CLOSED'),
    make(currentQuarter, year, 'ACTIVE'),
    make(nextQuarter, nextYear, 'UPCOMING'),
  ];
}

function buildProfiles(now: Date): Profile[] {
  const profiles: Profile[] = [];

  const base = (
    id: string,
    alias: string,
    role: Profile['role'],
    gender: GenderCategory,
    weight: number,
    country: string,
    city: string,
    daysOld: number,
  ): Profile => ({
    id,
    alias,
    avatar_url: null,
    language: country === 'ES' || country === 'MX' || country === 'AR' || country === 'CO' ? 'es' : 'en',
    country,
    city,
    birth_date: `${now.getUTCFullYear() - intBetween(18, 38)}-0${intBetween(1, 9)}-1${intBetween(0, 9)}`,
    gender_category: gender,
    body_weight_kg: Math.round(weight * 10) / 10,
    weight_class: assignWeightClass(weight, gender),
    weight_updated_at: iso(daysAgo(now, intBetween(35, 200))),
    location_updated_at: iso(daysAgo(now, intBetween(95, 300))),
    role,
    season_points: 0,
    rank_level: 1,
    best_rank_level: 1,
    privacy_level: rand() > 0.9 ? 'RESTRICTED' : 'PUBLIC',
    instagram_account_id: rand() > 0.7 ? `ig_${id}` : null,
    tiktok_account_id: rand() > 0.85 ? `tt_${id}` : null,
    email_verified: true,
    onboarding_completed: true,
    sounds_enabled: false,
    email_competitions: true,
    created_at: iso(daysAgo(now, daysOld)),
    deleted_at: null,
  });

  // Cuentas fijas para probar los tres roles desde la pantalla de acceso.
  profiles.push(base('demo-admin', 'Leonidas', 'ADMIN', 'M', 79.4, 'ES', 'Madrid', 240));
  profiles.push(base('demo-mod', 'KryptEye', 'MODERATOR', 'F', 58.2, 'ES', 'Barcelona', 210));
  profiles.push(base('demo-athlete', 'HoplonRise', 'ATHLETE', 'M', 72.6, 'ES', 'Valencia', 180));

  for (let i = 0; i < 45; i += 1) {
    const location = pick(LOCATIONS);
    const genderRoll = rand();
    const gender: GenderCategory = genderRoll > 0.93 ? 'OPEN' : genderRoll > 0.34 ? 'M' : 'F';
    const weight =
      gender === 'F' ? between(48, 78) : between(60, 96);
    profiles.push(
      base(
        `athlete-${i}`,
        makeAlias(i),
        'ATHLETE',
        gender,
        weight,
        location.country,
        pick(location.cities),
        intBetween(5, 200),
      ),
    );
  }

  return profiles;
}

function buildCompetitions(
  now: Date,
  season: Season,
  exercises: Exercise[],
  profiles: Profile[],
): { competitions: Competition[]; enrollments: Enrollment[]; submissions: Submission[] } {
  const competitions: Competition[] = [];
  const enrollments: Enrollment[] = [];
  const submissions: Submission[] = [];

  const currentFortnight = seasonFortnightNumber(season, now);
  const calendar = seasonFortnights(season);

  // Tres quincenas cerradas y publicadas, la actual abierta y una programada.
  const fortnightOffsets = [-3, -2, -1, 0, 1];

  for (const offset of fortnightOffsets) {
    const fortnightNumber = currentFortnight + offset;
    if (fortnightNumber < 1 || fortnightNumber > FORTNIGHTS_PER_SEASON) continue;

    const { startsAt, deadline, publishAt, isFinalFortnight } = calendar[fortnightNumber - 1];

    const status: Competition['status'] =
      offset < 0 ? 'PUBLISHED' : offset === 0 ? 'ACTIVE' : 'SCHEDULED';

    // 4 competiciones por quincena con temáticas distintas (RF-COMP-07). La
    // rotación de tres quincenas garantiza que siempre haya una dinámica, una
    // estática, una con lastre y una de resistencia, y que las pruebas de
    // élite —estáticos duros, flexión en planche, front lever touch— salgan
    // con regularidad en vez de quedarse de adorno en el catálogo.
    const rotation = ((offset % 3) + 3) % 3;
    const picks = [
      // [STATIC, DYNAMIC, ENDURANCE]
      [0, 1, 3], // front lever · dominadas estrictas · AMRAP de fondos
      [5, 11, 4], // pino libre · pino-flexiones · 100 dominadas por tiempo
      [8, 10, 3], // estáticos de élite · front lever touch · AMRAP de fondos
    ][rotation];

    const roundTemplates = [
      TEMPLATES[picks[0]],
      TEMPLATES[picks[1]],
      TEMPLATES[2], // la de lastre es fija: es la única DYNAMIC_WEIGHTED
      TEMPLATES[picks[2]],
    ];

    for (const template of roundTemplates) {
      const competition: Competition = {
        id: `comp-f${fortnightNumber}-${template.slug}`,
        season_id: season.id,
        slug: `${template.slug}-s${season.year}q${season.quarter}f${fortnightNumber}`,
        title_i18n: template.title,
        description_i18n: template.description,
        rules_i18n: template.rules,
        theme: template.theme,
        measurement_type:
          template.theme === 'DYNAMIC'
            ? 'REPS'
            : template.theme === 'ENDURANCE'
              ? 'REPS_OR_TIME'
              : 'SECONDS',
        endurance_format: template.enduranceFormat ?? null,
        endurance_target_seconds: template.enduranceTargetSeconds ?? null,
        allows_weight:
          template.theme === 'DYNAMIC' || template.theme === 'DYNAMIC_WEIGHTED',
        requires_weight: template.theme === 'DYNAMIC_WEIGHTED',
        allowed_exercise_ids: template.exerciseCodes.map((code) => `ex-${code}`),
        active_categories: ['M', 'F', 'OPEN'],
        hashtag: template.hashtag,
        cover_image_url: null,
        starts_at: iso(startsAt),
        submission_deadline: iso(deadline),
        results_published_at: status === 'PUBLISHED' ? iso(publishAt) : null,
        score_multiplier: isFinalFortnight ? 2 : 1,
        status,
        fortnight_number: fortnightNumber,
        participant_count: 0,
      };

      if (status !== 'SCHEDULED') {
        const participants = profiles.filter(() => rand() < (status === 'ACTIVE' ? 0.45 : 0.55));
        competition.participant_count = participants.length;

        for (const profile of participants) {
          enrollments.push({
            id: `enr-${competition.id}-${profile.id}`,
            user_id: profile.id,
            competition_id: competition.id,
            gender_category: profile.gender_category,
            weight_class: profile.weight_class,
            created_at: iso(new Date(startsAt.getTime() + intBetween(1, 40) * 3600 * 1000)),
          });

          // No todo el que se inscribe acaba enviando (RF-COMP-04).
          if (rand() > (status === 'ACTIVE' ? 0.45 : 0.15)) continue;

          submissions.push(
            makeSubmission(now, competition, profile, exercises, status === 'PUBLISHED'),
          );
        }
      }

      competitions.push(competition);
    }
  }

  return { competitions, enrollments, submissions };
}

function makeSubmission(
  now: Date,
  competition: Competition,
  profile: Profile,
  exercises: Exercise[],
  isClosed: boolean,
): Submission {
  const exercise =
    exercises.find((e) => e.id === pick(competition.allowed_exercise_ids)) ?? exercises[0];

  const startsAt = new Date(competition.starts_at);
  // Se reparte el envio a lo largo de la quincena (el cierre llega a las 333 h),
  // pero nunca despues de "ahora": en la competicion abierta un envio con fecha
  // futura se veria como "dentro de 3 dias" en el historial.
  const createdAt = new Date(
    Math.min(startsAt.getTime() + intBetween(4, 320) * 3600 * 1000, now.getTime()),
  );

  // Marca verosimil segun la tematica y el coeficiente del ejercicio.
  let reps: number | null = null;
  let seconds: number | null = null;
  const difficulty = exercise.difficulty_coefficient;
  const reference = exercise.reference_mark;

  // Las marcas se generan como fraccion de la referencia del ejercicio: es lo
  // unico que produce numeros creibles tanto en flexiones como en victorian,
  // donde una marca buena son 3 segundos.
  const fraction = () => between(0.45, 1.45);

  if (competition.theme === 'DYNAMIC') {
    reps = Math.max(1, Math.round(reference * fraction()));
  } else if (competition.theme === 'ENDURANCE') {
    if (competition.endurance_format === 'FOR_TIME') {
      // Aqui menos es mejor: la fraccion va sobre el tiempo empleado.
      seconds = Math.max(30, Math.round(reference * between(0.7, 1.6)));
    } else {
      reps = Math.max(1, Math.round(reference * fraction()));
    }
  } else {
    seconds = Math.max(1, Math.round(reference * fraction()));
  }

  // El lastre solo existe donde se cuentan repeticiones.
  const canUseWeight = competition.allows_weight;
  const mustUseWeight = competition.requires_weight;
  const addedWeight = mustUseWeight
    ? Math.round(between(5, 32))
    : canUseWeight && rand() > 0.65
      ? Math.round(between(5, 20))
      : null;

  const breakdown = computeScore({
    difficulty,
    reference,
    theme: competition.theme,
    performance: {
      theme: competition.theme,
      reps,
      seconds,
      enduranceFormat: competition.endurance_format,
      targetSeconds: competition.endurance_target_seconds,
    },
    addedWeightKg: addedWeight,
    bodyWeightKg: profile.body_weight_kg,
    competitionMultiplier: competition.score_multiplier,
  });

  // Distribucion de estados realista para que la cola de revision tenga
  // material y el historial del atleta ensene los seis estados posibles.
  const roll = rand();
  let status: Submission['status'];
  if (isClosed) {
    status = roll > 0.12 ? 'APROBADO' : roll > 0.04 ? 'RECHAZADO' : 'INVALIDADO';
  } else {
    status = roll > 0.55 ? 'PENDIENTE' : roll > 0.45 ? 'EN_REVISION' : 'APROBADO';
  }

  const approved = status === 'APROBADO';
  const platform = rand() > 0.45 ? 'INSTAGRAM' : 'TIKTOK';
  const postId =
    platform === 'INSTAGRAM'
      ? `C${rand().toString(36).slice(2, 12)}`
      : `73${Math.floor(rand() * 1e16)}`;

  return {
    id: `sub-${competition.id}-${profile.id}`,
    user_id: profile.id,
    competition_id: competition.id,
    exercise_id: exercise.id,
    exercise_coefficient_snapshot: difficulty,
    exercise_reference_snapshot: reference,
    gender_category: profile.gender_category,
    weight_class: profile.weight_class,
    platform,
    post_url:
      platform === 'INSTAGRAM'
        ? `https://www.instagram.com/reel/${postId}/`
        : `https://www.tiktok.com/@${profile.alias.toLowerCase()}/video/${postId}`,
    post_external_id: `${platform.toLowerCase()}:${postId}`,
    embed_html: null,
    declared_reps: reps,
    declared_seconds: seconds,
    added_weight_kg: addedWeight,
    body_weight_snapshot_kg: profile.body_weight_kg,
    weight_multiplier: breakdown.weightMultiplier,
    provisional_score: breakdown.total,
    final_score: approved ? breakdown.total : null,
    score_breakdown_json: approved ? breakdown : null,
    status,
    rejection_reason:
      status === 'RECHAZADO'
        ? pick(['VIDEO_CUT', 'UNCOUNTABLE_REPS', 'OUT_OF_RULES', 'WEIGHT_NOT_VISIBLE'] as const)
        : null,
    rejection_comment:
      status === 'RECHAZADO'
        ? 'El plano corta la parte baja del recorrido; repite con el cuerpo entero en cuadro.'
        : null,
    admin_note: null,
    reviewed_by: status === 'APROBADO' || status === 'RECHAZADO' ? 'demo-admin' : null,
    reviewed_at:
      status === 'APROBADO' || status === 'RECHAZADO'
        ? iso(new Date(createdAt.getTime() + intBetween(2, 40) * 3600 * 1000))
        : null,
    auto_detected: rand() > 0.8,
    notes: null,
    created_at: iso(createdAt),
    updated_at: iso(createdAt),
  };
}

function buildUserBadges(
  now: Date,
  profiles: Profile[],
  competitions: Competition[],
  submissions: Submission[],
  badges: Badge[],
): UserBadge[] {
  const userBadges: UserBadge[] = [];
  const badgeByCode = new Map(badges.map((b) => [b.code, b]));

  // Podios de las competiciones ya publicadas, por categoria (RF-CAT-05).
  for (const competition of competitions) {
    if (competition.status !== 'PUBLISHED') continue;

    const approved = submissions.filter(
      (s) => s.competition_id === competition.id && s.status === 'APROBADO',
    );
    const groups = new Map<string, Submission[]>();
    for (const submission of approved) {
      const key = `${submission.gender_category}|${submission.weight_class}`;
      groups.set(key, [...(groups.get(key) ?? []), submission]);
    }

    for (const [, group] of groups) {
      const podium = [...group].sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0));
      const codes = ['laurel_crown', 'silver_shield', 'bronze_shield'];
      podium.slice(0, 3).forEach((submission, index) => {
        const badge = badgeByCode.get(codes[index]);
        if (!badge) return;
        userBadges.push({
          id: `ub-${competition.id}-${submission.user_id}-${index}`,
          user_id: submission.user_id,
          badge_id: badge.id,
          competition_id: competition.id,
          season_id: competition.season_id,
          gender_category: submission.gender_category,
          weight_class: submission.weight_class,
          awarded_at: competition.results_published_at ?? iso(now),
          featured: index === 0,
        });
      });
    }
  }

  // Primera Sangre para todo el que tenga algun envio aprobado.
  const firstBlood = badgeByCode.get('first_blood');
  if (firstBlood) {
    const withApproved = new Set(
      submissions.filter((s) => s.status === 'APROBADO').map((s) => s.user_id),
    );
    for (const userId of withApproved) {
      userBadges.push({
        id: `ub-first-blood-${userId}`,
        user_id: userId,
        badge_id: firstBlood.id,
        competition_id: null,
        season_id: null,
        gender_category: null,
        weight_class: null,
        awarded_at: iso(daysAgo(now, intBetween(10, 60))),
        featured: false,
      });
    }
  }

  // Un par de insignias de racha para dar vida a las vitrinas.
  const spartanWall = badgeByCode.get('spartan_wall');
  if (spartanWall) {
    for (const profile of profiles.slice(0, 6)) {
      userBadges.push({
        id: `ub-muralla-${profile.id}`,
        user_id: profile.id,
        badge_id: spartanWall.id,
        competition_id: null,
        season_id: null,
        gender_category: null,
        weight_class: null,
        awarded_at: iso(daysAgo(now, intBetween(5, 40))),
        featured: false,
      });
    }
  }

  // --- La Cima: exclusiva del primero del ranking global -------------------
  //
  // Se concede al final, cuando los puntos de todos los perfiles ya estan
  // calculados. A partir de aqui la mantiene `reassignSummitBadge` en el
  // repositorio, que la traspasa en cuanto alguien adelanta al lider.
  const summit = badgeByCode.get('the_summit');
  const leader = [...profiles]
    .filter((p) => !p.deleted_at && p.season_points > 0)
    .sort((a, b) => b.season_points - a.season_points)[0];

  if (summit && leader) {
    userBadges.push({
      id: `ub-summit-${leader.id}`,
      user_id: leader.id,
      badge_id: summit.id,
      competition_id: null,
      season_id: null,
      gender_category: leader.gender_category,
      weight_class: leader.weight_class,
      awarded_at: iso(daysAgo(now, intBetween(1, 6))),
      featured: true,
    });
  }

  return userBadges;
}

function buildFollows(profiles: Profile[]): Follow[] {
  const follows: Follow[] = [];
  const me = profiles.find((p) => p.id === 'demo-athlete');
  if (!me) return follows;

  for (const profile of profiles.slice(3, 12)) {
    if (rand() > 0.5) {
      follows.push({
        id: `follow-${me.id}-${profile.id}`,
        follower_id: me.id,
        followed_id: profile.id,
        created_at: new Date().toISOString(),
      });
    }
  }
  return follows;
}

/**
 * Unos cuantos mensajes en el tablon, para que el foro no se estrene vacio en
 * la demostracion. Las fechas van hacia atras desde hoy para que el orden se
 * vea de verdad, y uno de los hilos lleva respuesta.
 */
function demoForumPosts(profiles: Profile[]): ForumPost[] {
  const autores = ['demo-admin', 'demo-athlete', 'demo-mod']
    .map((id) => profiles.find((p) => p.id === id))
    .filter((p): p is Profile => Boolean(p));

  if (autores.length === 0) return [];

  const mensajes: { autor: Profile; texto: string; horas: number; respondeA?: string }[] = [
    {
      autor: autores[0],
      texto:
        'Abierta la quincena. Si es vuestra primera vez: grabad el video entero, sin cortes, y que se vea el cuerpo completo.',
      horas: 6,
    },
    {
      autor: autores[1] ?? autores[0],
      texto: '¿Alguien entrena en Madrid por la zona norte? Busco gente para ir al parque los sabados.',
      horas: 28,
    },
    {
      autor: autores[2] ?? autores[0],
      texto: 'Yo voy casi todos los sabados por la manana. Avisa y nos vemos alli.',
      horas: 26,
      respondeA: 'forum-demo-2',
    },
    {
      autor: autores[1] ?? autores[0],
      texto: 'Consejo para el front lever: antes de la posicion completa, aguanta con una pierna estirada. Se nota en dos semanas.',
      horas: 52,
    },
  ];

  const ahora = Date.now();
  return mensajes.map((mensaje, indice) => ({
    id: `forum-demo-${indice + 1}`,
    parent_id: mensaje.respondeA ?? null,
    author_id: mensaje.autor.id,
    author_alias: mensaje.autor.alias,
    author_rank_level: mensaje.autor.rank_level,
    body: mensaje.texto,
    hidden: false,
    created_at: new Date(ahora - mensaje.horas * 3600 * 1000).toISOString(),
  }));
}

function buildAuditLog(
  now: Date,
  profiles: Profile[],
  competitions: Competition[],
): AuditLogEntry[] {
  const admin = profiles.find((p) => p.role === 'ADMIN')!;
  const published = competitions.filter((c) => c.status === 'PUBLISHED').slice(0, 4);

  return published.map((competition, index) => ({
    id: `audit-${index}`,
    actor_id: admin.id,
    actor_alias: admin.alias,
    action: 'PUBLISH_RESULTS',
    entity_type: 'competition',
    entity_id: competition.id,
    before_json: { status: 'CLOSED', results_published_at: null },
    after_json: { status: 'PUBLISHED', results_published_at: competition.results_published_at },
    created_at: competition.results_published_at ?? iso(daysAgo(now, index + 1)),
  }));
}
